import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { themeClasses } from '../../utils/themeClasses';
import {
  Trophy,
  Calendar,
  Users,
  ArrowRight,
  Filter,
  Medal,
  Sparkles,
  Award,
  Crown,
  Star,
  ChevronDown,
  ChevronUp,
  Shield,
  Target,
  Search
} from 'lucide-react';
import { championsApi } from '../../services/api';
import { LoadingSpinner, EmptyStates } from '../Common';
import { useSite } from '../../contexts/SiteContext';

interface Champion {
  id: string;
  team_name: string;
  team_city: string;
  team_abbreviation: string;
  championship_name: string;
  season_name: string;
  year: number;
  record: string;
  coach: string;
}

const ChampionshipHistory: React.FC = () => {
  const site = useSite();
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedChampion, setExpandedChampion] = useState<string | null>(null);

  const { data: champions, isLoading, error } = useQuery({
    queryKey: ['champions', selectedYear, site.sport, site.level, searchTerm],
    queryFn: async () => {
      const filters = site.getApiFilters();
      if (selectedYear) {
        return await championsApi.getByYear(selectedYear, filters);
      }
      return await championsApi.getAll(filters);
    },
  });

  if (isLoading) {
    return <LoadingSpinner message="Loading championship history..." />;
  }

  if (error) {
    return <EmptyStates.LoadingError error="Failed to load championship history" onRetry={() => window.location.reload()} />;
  }

  if (!champions?.data || champions.data.length === 0) {
    return <EmptyStates.NoData message="No championship history available" />;
  }

  // Filter champions by search term
  const filteredChampions = champions.data.filter((champion: Champion) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      champion.team_name.toLowerCase().includes(term) ||
      champion.team_city.toLowerCase().includes(term) ||
      champion.championship_name.toLowerCase().includes(term) ||
      champion.coach.toLowerCase().includes(term)
    );
  });

  // Group champions by year and championship type
  const championsByYear = filteredChampions.reduce((acc: Record<number, Record<string, Champion[]>>, champion: Champion) => {
    if (!acc[champion.year]) {
      acc[champion.year] = {};
    }

    // For site-specific views, group by championship name instead of sport
    let groupKey: string;
    if (site.isSportSpecific) {
      // For sport-specific sites, group by championship name
      groupKey = champion.championship_name;
    } else {
      // For main site, group by sport
      const sport = champion.championship_name.includes('Super Bowl') ||
                    champion.championship_name.includes('NFL') ? 'Football' :
                    champion.championship_name.includes('NBA') ||
                    champion.championship_name.includes('Basketball') ? 'Basketball' :
                    champion.championship_name.includes('World Series') ||
                    champion.championship_name.includes('MLB') ? 'Baseball' :
                    champion.championship_name.includes('Stanley Cup') ||
                    champion.championship_name.includes('NHL') ? 'Hockey' : 'Other';
      groupKey = sport;
    }

    if (!acc[champion.year][groupKey]) {
      acc[champion.year][groupKey] = [];
    }

    acc[champion.year][groupKey].push(champion);
    return acc;
  }, {});

  const years = Object.keys(championsByYear).map(Number).sort((a, b) => b - a);

  // For sport-specific sites, use championship names; otherwise use sport names
  const categories = site.isSportSpecific
    ? [...new Set(filteredChampions.map((c: Champion) => c.championship_name))].sort()
    : ['Football', 'Basketball', 'Baseball', 'Hockey'];

  const getSportIcon = (category: string) => {
    // Check for specific championship names first
    if (category.includes('Super Bowl') || category === 'Football') return <Trophy className="w-5 h-5" />;
    if (category.includes('NBA') || category === 'Basketball') return <Medal className="w-5 h-5" />;
    if (category.includes('World Series') || category === 'Baseball') return <Award className="w-5 h-5" />;
    if (category.includes('Stanley Cup') || category === 'Hockey') return <Crown className="w-5 h-5" />;
    return <Star className="w-5 h-5" />;
  };

  const getSportColor = (category: string) => {
    // Use theme-aware gradients instead of hardcoded colors
    return `bg-gradient-to-br ${themeClasses.sportBgPrimary} border border-[var(--border-primary)]`;
  };

  const getChampionshipImportance = (championship: string) => {
    if (championship.includes('Super Bowl') ||
        championship.includes('NBA Championship') ||
        championship.includes('World Series') ||
        championship.includes('Stanley Cup')) {
      return 'primary';
    }
    return 'secondary';
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen py-12">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full border border-primary/30 mb-6"
            >
              <Trophy className="w-4 h-4 text-primary" />
              <span className="text-primary text-xs font-semibold uppercase tracking-wider">
                {site.isSportSpecific ? `${site.name} History` : 'Championship History'}
              </span>
            </motion.div>

            <h1 className="text-4xl md:text-6xl font-black mb-4">
              {site.isSportSpecific ? (
                <>
                  <span className="text-white">{site.sport === 'football' && site.level === 'professional' ? 'SUPER BOWL ' : site.name.toUpperCase() + ' '}</span>
                  <span className={themeClasses.neonText}>CHAMPIONS</span>
                </>
              ) : (
                <>
                  <span className="text-white">CHAMPIONSHIP </span>
                  <span className={themeClasses.neonText}>HISTORY</span>
                </>
              )}
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {site.isSportSpecific
                ? `Complete history of ${site.sport === 'football' && site.level === 'professional' ? 'Super Bowl' : site.name} champions`
                : 'Explore the rich history of champions across all sports'}
            </p>
          </div>

          {/* Filters */}
          <div className="glass-card p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by team, city, or coach..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-surface/50 border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="relative flex-1 sm:flex-none sm:min-w-[200px]">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={selectedYear || ''}
                  onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
                  className="w-full pl-10 pr-12 py-3 bg-surface/50 border border-border rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-primary transition-colors"
                >
                  <option value="">All Years</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 text-center"
            >
              <Trophy className="w-8 h-8 text-primary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {filteredChampions.length}
              </div>
              <div className="text-sm text-gray-400">Total Championships</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 text-center"
            >
              <Shield className="w-8 h-8 text-secondary mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {new Set(filteredChampions.map((c: Champion) => c.team_name)).size}
              </div>
              <div className="text-sm text-gray-400">Unique Teams</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 text-center"
            >
              <Calendar className="w-8 h-8 text-accent mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {years.length}
              </div>
              <div className="text-sm text-gray-400">Years Tracked</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 text-center"
            >
              <Users className="w-8 h-8 text-success mx-auto mb-3" />
              <div className="text-3xl font-bold text-white mb-1">
                {new Set(filteredChampions.map((c: Champion) => c.coach)).size}
              </div>
              <div className="text-sm text-gray-400">Winning Coaches</div>
            </motion.div>
          </div>

          {/* Championship History */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-8"
          >
            {years.length > 0 ? (
              years.map((year, yearIndex) => (
                <motion.div
                  key={year}
                  variants={item}
                  className="space-y-6"
                >
                  {/* Year Header */}
                  <div className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-white">{year}</div>
                    <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
                  </div>

                  {/* Categories for this year */}
                  <div className={`grid grid-cols-1 ${site.isSportSpecific ? 'md:grid-cols-1 lg:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-4'} gap-6`}>
                    {categories.map(category => {
                      const categoryChampions = championsByYear[year][category] || [];

                      if (categoryChampions.length === 0) {
                        return null;
                      }

                      return (
                        <div key={category} className={`glass-card p-6 ${getSportColor(category)}`}>
                          {/* Category Header */}
                          <div className="flex items-center gap-3 mb-4">
                            {getSportIcon(category)}
                            <h3 className="text-lg font-semibold text-white">{category}</h3>
                          </div>

                          {/* Champions */}
                          <div className="space-y-3">
                            {categoryChampions.map((champion, index) => {
                              const isExpanded = expandedChampion === `${year}-${category}-${index}`;
                              const isPrimary = getChampionshipImportance(champion.championship_name) === 'primary';

                              return (
                                <motion.div
                                  key={`${champion.id}-${index}`}
                                  initial={{ opacity: 0, scale: 0.95 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: index * 0.05 }}
                                  className={`relative overflow-hidden rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                                    isPrimary
                                      ? 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 shadow-lg'
                                      : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800/70'
                                  }`}
                                  onClick={() => setExpandedChampion(isExpanded ? null : `${year}-${category}-${index}`)}
                                >
                                  {isPrimary && (
                                    <div className="absolute top-2 right-2">
                                      <Sparkles className="w-4 h-4 text-yellow-400" />
                                    </div>
                                  )}

                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-semibold text-white text-sm mb-1">
                                        {champion.championship_name}
                                      </div>
                                      <div className="text-primary font-bold text-lg">
                                        {champion.team_name}
                                      </div>
                                      <div className="text-gray-400 text-sm">
                                        {champion.record}
                                      </div>
                                    </div>
                                    <div className="ml-2">
                                      {isExpanded ? (
                                        <ChevronUp className="w-4 h-4 text-gray-400" />
                                      ) : (
                                        <ChevronDown className="w-4 h-4 text-gray-400" />
                                      )}
                                    </div>
                                  </div>

                                  {isExpanded && (
                                    <motion.div
                                      initial={{ opacity: 0, height: 0 }}
                                      animate={{ opacity: 1, height: 'auto' }}
                                      exit={{ opacity: 0, height: 0 }}
                                      className="mt-3 pt-3 border-t border-gray-600"
                                    >
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                          <span className="text-gray-400">Coach:</span>
                                          <div className="text-white">{champion.coach}</div>
                                        </div>
                                        <div>
                                          <span className="text-gray-400">Season:</span>
                                          <div className="text-white">{champion.season_name}</div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-xl text-gray-400">No champions found matching your search</p>
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default ChampionshipHistory;